import { SignInIdentifier, type VerificationCodeIdentifier } from '@logto/schemas';
import { t } from 'i18next';
import { useContext } from 'react';
import { useParams } from 'react-router-dom';

import SecondaryPageLayout from '@/Layout/SecondaryPageLayout';
import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import WhatsAppBadge from '@/components/WhatsAppBadge';
import VerificationCodeContainer from '@/containers/VerificationCode';
import { useSieMethods } from '@/hooks/use-sie';
import ErrorPage from '@/pages/ErrorPage';
import { type IdentifierInputValue } from '@/shared/components/InputFields/SmartInputField';
import { UserFlow } from '@/types';
import { userFlowGuard } from '@/types/guard';
import { formatPhoneNumberWithCountryCallingCode } from '@/utils/country-code';
import { codeVerificationTypeMap } from '@/utils/sign-in-experience';

import EmphasizedTarget from './EmphasizedTarget';

type Parameters = {
  flow: string;
};

const isValidVerificationCodeIdentifier = (
  identifierInputValue: IdentifierInputValue | undefined
): identifierInputValue is VerificationCodeIdentifier =>
  Boolean(
    identifierInputValue?.type &&
      identifierInputValue.type !== SignInIdentifier.Username &&
      identifierInputValue.value
  );

const VerificationCode = () => {
  const { flow } = useParams<Parameters>();
  const { signInMethods, phoneVerificationChannel } = useSieMethods();

  const { identifierInputValue, forgotPasswordIdentifierInputValue, verificationIdsMap } =
    useContext(UserInteractionContext);

  const { data: userFlow } = userFlowGuard.safeParse(flow);

  if (!userFlow) {
    return <ErrorPage />;
  }

  const cachedIdentifierInputValue =
    flow === UserFlow.ForgotPassword ? forgotPasswordIdentifierInputValue : identifierInputValue;

  if (!isValidVerificationCodeIdentifier(cachedIdentifierInputValue)) {
    return <ErrorPage title="error.invalid_session" />;
  }

  const { type, value } = cachedIdentifierInputValue;

  const methodSettings = signInMethods.find((method) => method.identifier === type);
  const hasPasswordButton = userFlow === UserFlow.SignIn && methodSettings?.password;

  // VerificationId not found
  const verificationId = verificationIdsMap[codeVerificationTypeMap[type]];
  if (!verificationId) {
    return <ErrorPage title="error.invalid_session" />;
  }

  // WhatsApp is an SMS-type connector, so a "phone" code may actually arrive over WhatsApp.
  const isWhatsApp = type === SignInIdentifier.Phone && phoneVerificationChannel === 'whatsapp';

  return (
    <SecondaryPageLayout
      title={isWhatsApp ? 'description.verify_phone_via_whatsapp' : `description.verify_${type}`}
      titleIcon={isWhatsApp ? <WhatsAppBadge /> : undefined}
      description={
        <EmphasizedTarget
          phrase={
            isWhatsApp ? 'description.enter_passcode_via_whatsapp' : 'description.enter_passcode'
          }
          address={t(`description.${type === SignInIdentifier.Email ? 'email' : 'phone_number'}`)}
          target={
            type === SignInIdentifier.Phone ? formatPhoneNumberWithCountryCallingCode(value) : value
          }
        />
      }
    >
      <VerificationCodeContainer
        flow={userFlow}
        identifier={cachedIdentifierInputValue}
        verificationId={verificationId}
        hasPasswordButton={hasPasswordButton}
      />
    </SecondaryPageLayout>
  );
};

export default VerificationCode;
